import React, { useState, useEffect, useCallback } from 'react';
import { DiscountCode, DiscountUsage, Instructor } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDate, parseLocalDate } from '../../utils/dateUtils';

const ManageDiscountCodes: React.FC = () => {
  usePageTitle('Manage Discount Codes');
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [usageHistory, setUsageHistory] = useState<DiscountUsage[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'codes' | 'commissions'>('codes');
  const authFetch = useAuthFetch();

  const [formData, setFormData] = useState({
    code: '',
    instructorId: '',
    discountValue: 10,
    active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [codesRes, usageRes, instRes] = await Promise.all([
        authFetch('/api/discounts'),
        authFetch('/api/discounts/usage'),
        authFetch('/api/instructors'),
      ]);
      if (codesRes.ok) setDiscountCodes(await codesRes.json());
      if (usageRes.ok) setUsageHistory(await usageRes.json());
      if (instRes.ok) setInstructors(await instRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const instructorMap = instructors.reduce<Record<string, Instructor>>((acc, i) => {
    acc[i.id] = i;
    return acc;
  }, {});

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await authFetch('/api/discounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          discountType: 'percentage',
        }),
      });
      if (res.ok) {
        await fetchData();
        setShowForm(false);
        setFormData({ code: '', instructorId: '', discountValue: 10, active: true });
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to create discount code');
      }
    } catch {
      setError('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (code: string, active: boolean) => {
    try {
      await authFetch(`/api/discounts/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !active }),
      });
      fetchData();
    } catch (err) {
      console.error('Error toggling discount:', err);
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await authFetch(`/api/discounts/${code}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      console.error('Error deleting discount:', err);
    }
  };

  // Commission summary by instructor
  const commissionByInstructor = usageHistory.reduce<
    Record<string, { total: number; count: number; totalDiscount: number }>
  >((acc, usage) => {
    const key = usage.discountCode;
    if (!acc[key]) acc[key] = { total: 0, count: 0, totalDiscount: 0 };
    acc[key].total += usage.commissionAmount;
    acc[key].count += 1;
    acc[key].totalDiscount += usage.discountAmount;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading discount codes...</p>
      </div>
    );
  }

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <div className="section-header-row">
            <h1 className="page-title">Discount Codes &amp; Commissions</h1>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Create Code</button>
          </div>
        </div>
      </section>

      {showForm && (
        <section className="section">
          <div className="section-container">
            <div className="admin-form card">
              <h2>Create Discount Code</h2>
              {error && <div className="form-error">{error}</div>}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Code *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.code}
                    onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., SAVE10"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Instructor *</label>
                  <select
                    className="form-input"
                    value={formData.instructorId}
                    onChange={(e) => setFormData((p) => ({ ...p, instructorId: e.target.value }))}
                  >
                    <option value="">Select instructor</option>
                    {instructors.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Percentage</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.discountValue}
                    onChange={(e) => setFormData((p) => ({ ...p, discountValue: parseInt(e.target.value) || 0 }))}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Creating...' : 'Create Code'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-container">
          <div className="tab-bar">
            <button
              className={`tab-btn ${activeTab === 'codes' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('codes')}
            >
              Discount Codes
            </button>
            <button
              className={`tab-btn ${activeTab === 'commissions' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('commissions')}
            >
              Commission Report
            </button>
          </div>

          {activeTab === 'codes' && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Instructor</th>
                  <th>Discount</th>
                  <th>Uses</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {discountCodes.map((dc) => {
                  const uses = usageHistory.filter((u) => u.discountCode === dc.code).length;
                  return (
                    <tr key={dc.code}>
                      <td><strong>{dc.code}</strong></td>
                      <td>{instructorMap[dc.instructorId]?.name || dc.instructorId}</td>
                      <td>{dc.discountValue}%</td>
                      <td>{uses}</td>
                      <td>{formatDate(dc.createdDate)}</td>
                      <td>
                        <span className={`status-badge ${dc.active ? 'status-active' : 'status-cancelled'}`}>
                          {dc.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="td-actions">
                        <button className="btn btn-text" onClick={() => handleToggle(dc.code, dc.active)}>
                          {dc.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="btn btn-text btn-danger" onClick={() => handleDelete(dc.code)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {activeTab === 'commissions' && (
            <>
              <div className="commission-summary">
                {Object.entries(commissionByInstructor).map(([code, data]) => {
                  const dc = discountCodes.find((d) => d.code === code);
                  const instructor = dc ? instructorMap[dc.instructorId] : null;
                  return (
                    <div key={code} className="commission-card">
                      <h3>{code}</h3>
                      <p className="commission-instructor">{instructor?.name || 'Unknown'}</p>
                      <div className="commission-stats">
                        <div>
                          <span className="commission-number">{data.count}</span>
                          <span className="commission-label">Uses</span>
                        </div>
                        <div>
                          <span className="commission-number">${data.totalDiscount.toFixed(2)}</span>
                          <span className="commission-label">Total Discounts</span>
                        </div>
                        <div>
                          <span className="commission-number">${data.total.toFixed(2)}</span>
                          <span className="commission-label">Commission Earned</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <h3 className="subsection-title">Usage History</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Customer</th>
                    <th>Tier</th>
                    <th>Discount</th>
                    <th>Commission</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usageHistory
                    .sort((a, b) => parseLocalDate(b.dateUsed).getTime() - parseLocalDate(a.dateUsed).getTime())
                    .map((usage) => (
                      <tr key={usage.id}>
                        <td><strong>{usage.discountCode}</strong></td>
                        <td>{usage.customerEmail}</td>
                        <td>{usage.subscriptionTier}</td>
                        <td>${usage.discountAmount.toFixed(2)}</td>
                        <td>${usage.commissionAmount.toFixed(2)}</td>
                        <td>{formatDate(usage.dateUsed)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          {activeTab === 'codes' && discountCodes.length === 0 && (
            <p className="empty-text">No discount codes created yet.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManageDiscountCodes;
